<template>
    <div>
        <div class="questionnaire-title">
            {{ questionnaireResults.title }}
        </div>
        <Loader v-if="isLoading" :loader="true" />
        <div v-if="hasSomeResultToShow && !isLoading">
            <div
                class="mb-2"
                :key="categoryResult.category ? categoryResult.category.id : 'empty'"
                v-for="categoryResult in questionnaireResults.results"
            >
                <Category
                    :result="categoryResult"
                    :collapseId="`category-${
                        categoryResult.category ? categoryResult.category.id : 'empty'
                    }-${questionnaireId}`"
                />
                <b-collapse
                    :id="`category-${
                        categoryResult.category ? categoryResult.category.id : 'empty'
                    }-${questionnaireId}`"
                >
                    <div
                        class="subject-results-container"
                        :key="valueResult.value.id"
                        v-for="valueResult in categoryResult.values"
                    >
                        <Subject
                            class="mb-2"
                            :result="valueResult"
                            :collapseId="`subject-${valueResult.value.id}-${questionnaireId}`"
                        />
                        <b-collapse :id="`subject-${valueResult.value.id}-${questionnaireId}`">
                            <div class="questions" v-if="valueResult.key_behaviors.length">
                                <h4 class="title">
                                    {{ $t('values.questions') }}
                                </h4>
                                <Question
                                    :result="keyBehaviorResult"
                                    :key="keyBehaviorResult.key_behavior.id"
                                    v-for="keyBehaviorResult in valueResult.key_behaviors"
                                />
                            </div>
                            <div v-if="valueResult.comments.length">
                                <div class="title">
                                    {{ $tc('profile.results.comment', 0) }}
                                </div>
                                <Comments :comments="valueResult.comments" />
                            </div>
                        </b-collapse>
                    </div>

                    <div v-if="hasQuestionGraphic">
                        <score-by-subject-question-graph :values="categoryResult.values" />
                    </div>
                </b-collapse>
            </div>
            <GeneralComments
                v-if="hasGeneralComments"
                :questionnaireId="questionnaireId"
                :comments="questionnaireResults.generalComments"
            />
            <div v-if="hasNetworksResults">
                <div class="collapse-container" v-b-toggle="`network-${questionnaireId}`">
                    <div class="title">
                        {{ $t('values.networks') }}
                    </div>
                </div>
                <b-collapse :id="`network-${questionnaireId}`">
                    <Networks :valuers="questionnaireResults.valuers" />
                </b-collapse>
            </div>
            <div v-if="hasSubjectGraphic">
                <div class="collapse-container" v-b-toggle="`graphics-${questionnaireId}`">
                    <div class="title">
                        {{ $t('values.graphics') }}
                    </div>
                </div>
                <b-collapse :id="`graphics-${questionnaireId}`" class="graphics">
                    <SubjectsScoreByNetwork :filters="filterByNetwork" :height="400" :size="125" />
                    <SubjectsAverages :filters="filters" :height="400" :size="125" />
                </b-collapse>
            </div>
        </div>
        <b-alert v-if="!hasSomeResultToShow && !isLoading" class="text-center mb-0" show>{{
            $t('values.noResultsAvailable')
        }}</b-alert>
    </div>
</template>
<script>
import Avatar from 'vue-avatar'
import Networks from './Networks'
import Subject from './Subject'
import Comments from './Comments'
import Question from './Question'
import Category from './Category'
import SubjectsAverages from './SubjectsAverages'
import { SubjectsScoreByNetwork } from '@/views/reports/components/charts/components'
import { EventBuzz } from '@/main'
import { mapActions } from 'vuex'
import Loader from '@/views/components/loader'
import GeneralComments from './GeneralComments'
import LineChart from './LineChart.js'
import ScoreBySubjectQuestionGraph from './ScoreBySubjectQuestionGraph'

const VUEX_MODULE = 'questionnairesResults'

export default {
    components: {
        Loader,
        Avatar,
        Subject,
        Category,
        Question,
        Comments,
        Networks,
        GeneralComments,
        LineChart,
        ScoreBySubjectQuestionGraph,
        SubjectsScoreByNetwork,
        SubjectsAverages,
    },
    props: {
        questionnaireResults: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            filterByNetwork: {
                names: [this.user.name],
                questionnaires: [this.questionnaireResults.id],
            },
            filters: {
                user_id: [this.user.id],
                questionnaires: [this.questionnaireResults.id],
                departments: this.user.sections.map((item) => {
                    return item.id
                }),
                occupation_groups: this.user.occupation_groups.map((item) => {
                    return item.id
                }),
            },
        }
    },
    computed: {
        questionnaireId() {
            return this.questionnaireResults.id
        },
        hasSomeResultToShow() {
            return this.hasResults || this.hasGeneralComments
        },
        isLoading() {
            return this.questionnaireResults.isLoading
        },
        hasResults() {
            return this.questionnaireResults.results.length > 0
        },
        hasGeneralComments() {
            return this.questionnaireResults.generalComments.length > 0
        },
        hasNetworksResults() {
            return this.questionnaireResults.valuers.length > 0
        },
        hasSubjectGraphic() {
            return this.questionnaireResults.hasSubjectGraphic
        },
        hasQuestionGraphic() {
            return this.questionnaireResults.hasQuestionGraphic
        },
    },
    created() {
        EventBuzz.$on('spontaneous-evaluation-sented', (questionnaireId) => {
            if (this.questionnaireId == questionnaireId) {
                this.getResults()
            }
        })
    },
    mounted() {
        this.getResults()
    },
    methods: {
        ...mapActions(VUEX_MODULE, ['getQuestionnaireResultsData']),
        getResults() {
            this.getQuestionnaireResultsData(this.questionnaireResults)
        },
    },
}
</script>

<style lang="sass" scoped>
$gray: #6E6E6E
$blue: #50DDDD

.collapse-container
    margin-top: 1rem
    cursor: pointer
    background-color: $blue
    border-radius: 5px
    outline: none
    padding: 25px 30px
    box-shadow: 0px 2.3383px 2.3383px rgba(0, 0, 0, 0.2)
    .title
        color: white

::v-deep .separator
    margin-right: 10px
    margin-left: 10px
    &:after
        content: '|'

::v-deep .score
    font-weight: 700
    text-align: justify

::v-deep .title
    color: $gray
    font-weight: 700
    font-size: 18px
    margin-bottom: 4px

.questionnaire-title
    @extend .title
    font-size: 20px

::v-deep .subject-results-container
    color: $gray
    border-radius: 2px
    padding: 6px 16px 6px 16px
    box-shadow: 0px 2.5px 2.5px rgba(0, 0, 0, 0.2)

    .question
        margin-bottom: 22px

        &:last-child
            margin-bottom: 12px

@media screen and (min-width: 800px)
    .graphics
        display: flex
        .report-container
            width: 100%
</style>
